import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ExportIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { CloseModalButton } from '../../../components/close-modal-button/index';
import { ModalWrapper } from '../../../components/modal-wrapper/index';
import {
  downloadCompanyStorageFile,
  downloadCompanyStorageFilesSequentially,
} from '../../../utils/company-storage-url.util';
import { getS3FileOriginalName } from '../../../utils/get-s3file-original-name.util';
import { getTranslation } from '../../../utils/get-translation.util';
import { FilesModal } from '../../events/modals/index';
import { formatDividendAmount, formatDividendPerShare } from '../utils/format-dividend-amount.util';

export type ReviewPayoutRow = {
  dividendPerShare: number;
  files: string[];
  shareAmount: number;
  shareClass: string;
  stakeholder: string;
  totalPayout: number;
};

type Props = {
  companyId: string;
  currencySymbol: string;
  isExporting?: boolean;
  onClose: () => void;
  onExport?: () => void;
  open: boolean;
  period: string;
  rows: ReviewPayoutRow[];
  subtitle: string;
};

// Grid from design 4865:38971 — 1152px content: 221 / 221 / 163 / 163 / 163 / 221.
const columnWidthClass = [
  'w-[19.2%]',
  'w-[19.2%]',
  'w-[14.1%]',
  'w-[14.1%]',
  'w-[14.1%]',
  'w-[19.2%]',
];

const headerCellClass =
  'h-11 border-b border-gray-200 px-4 py-3 text-left text-[12px] leading-[18px] font-medium text-[#172335]';
const bodyCellClass = 'h-[72px] truncate px-4 py-4 align-middle text-sm font-[450] text-gray-700';

const formatNumber = (value: number): string => formatDividendPerShare(value);

const fileLabel = (link: string): string => getS3FileOriginalName(link) || 'file';

const FilesCell: FC<{
  files: string[];
  onDownload: (link: string) => void;
  onOpen: () => void;
}> = ({ files, onDownload, onOpen }) => (
  <button
    className="flex max-w-full items-center gap-2 border-0 bg-transparent p-0 text-left"
    onClick={() => (files.length === 1 ? onDownload(files[0]) : onOpen())}
    type="button"
  >
    <span className="max-w-[180px] truncate text-sm font-[450] text-brand-700 underline">
      {fileLabel(files[0])}
    </span>
    {files.length > 1 && (
      <span className="shrink-0 text-sm font-[450] text-brand-700">+{files.length - 1}</span>
    )}
  </button>
);

export const DividendReviewPayoutModal: FC<Props> = ({
  companyId,
  currencySymbol,
  isExporting,
  onClose,
  onExport,
  open,
  period,
  rows,
  subtitle,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isCutOff, setCutOff] = useState(false);
  const [filesRow, setFilesRow] = useState<null | ReviewPayoutRow>(null);

  // The bottom fade only makes sense while rows stay hidden below the fold.
  const syncFade = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;

    const isScrollable = element.scrollHeight > element.clientHeight + 1;
    const isAtBottom = element.scrollHeight - element.scrollTop - element.clientHeight <= 1;
    setCutOff(isScrollable && !isAtBottom);
  }, []);

  // The modal animates in, so the scroll box has no height on the first paint — measure on the
  // next frame, and again whenever it resizes.
  useEffect(() => {
    const frame = requestAnimationFrame(syncFade);
    const element = scrollRef.current;
    if (!element) return () => cancelAnimationFrame(frame);

    const observer = new ResizeObserver(syncFade);
    observer.observe(element);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [syncFade, open, rows]);

  const downloadFile = (link: string) => void downloadCompanyStorageFile(companyId, link);

  const filesModalName = filesRow
    ? [period, filesRow.stakeholder, filesRow.shareClass].filter(Boolean).join(' — ')
    : '';

  return (
    <>
      <ModalWrapper
        // `FilesModal` caps itself at the 453px the design gives it (4949:4145) and adds an 8px
        // margin each side, but only fills a parent with a width to fill — under `w-fit` it
        // collapsed onto whichever file names it held. The cap keeps the box the size of its
        // content, which is what `side="CENTER"` translates by; a full-width one lands hard left.
        className="z-[71] w-full max-w-[469px]"
        control={{ onOpenChange: (next) => !next && setFilesRow(null), open: filesRow !== null }}
        onInteractOutside={() => setFilesRow(null)}
        overlayClassName="z-[70]"
        side="CENTER"
      >
        <FilesModal
          attachedFilesLabel={
            <AppFormattedMessage
              id="FILES_ATTACHED"
              values={{ count: filesRow?.files.length ?? 0 }}
            />
          }
          filesLinks={filesRow?.files ?? []}
          name={filesModalName}
          onClose={() => setFilesRow(null)}
          onDownloadFile={downloadFile}
          onSuccess={() => {
            void downloadCompanyStorageFilesSequentially(companyId, filesRow?.files ?? []);
            setFilesRow(null);
          }}
        />
      </ModalWrapper>
      <ModalWrapper
        // Above the Add Dividend sheet this opens over: sheet content sits at z-[51], so a default
        // z-50 overlay would slide under it and leave the flow behind undimmed.
        className="z-[61] w-full max-w-[1200px]"
        control={{ onOpenChange: (next) => !next && onClose(), open }}
        onInteractOutside={(event) => (filesRow ? event.preventDefault() : onClose())}
        overlayClassName="z-[60]"
        side="CENTER"
      >
        <div className="flex max-h-[80vh] w-full flex-col overflow-hidden rounded-2xl bg-white">
          <div className="flex shrink-0 items-start justify-between px-6 pb-6 pt-6">
            <div className="flex flex-col gap-1">
              <span className="text-xl font-semibold leading-8 text-gray-700">
                {getTranslation('REVIEW_PAYOUT')}
              </span>
              <span className="text-xs font-[450] text-gray-500">{subtitle}</span>
            </div>
            <CloseModalButton onClose={onClose} />
          </div>

          <div className="relative mx-6 mb-6 flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-gray-200">
            <div className="flex-1 overflow-auto" onScroll={syncFade} ref={scrollRef}>
              <table className="w-full table-fixed border-collapse">
                <thead className="sticky top-0 bg-gray-50">
                  <tr>
                    <th className={twMerge(headerCellClass, columnWidthClass[0])}>
                      {getTranslation('STAKEHOLDER')}
                    </th>
                    <th className={twMerge(headerCellClass, columnWidthClass[1])}>
                      {getTranslation('SHARE_CLASS')}
                    </th>
                    <th className={twMerge(headerCellClass, columnWidthClass[2])}>
                      {getTranslation('SHARE_AMOUNT')}
                    </th>
                    <th className={twMerge(headerCellClass, columnWidthClass[3])}>
                      {getTranslation('DIVIDEND_PER_SHARE')}
                    </th>
                    <th className={twMerge(headerCellClass, columnWidthClass[4])}>
                      {getTranslation('TOTAL_PAYOUT')}
                    </th>
                    <th className={twMerge(headerCellClass, columnWidthClass[5])}>
                      {getTranslation('ATTACHED_FILES_COLUMN')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr className="border-b border-gray-100" key={`${row.stakeholder}-${index}`}>
                      <td className={bodyCellClass} title={row.stakeholder}>
                        {row.stakeholder}
                      </td>
                      <td className={bodyCellClass} title={row.shareClass}>
                        {row.shareClass}
                      </td>
                      <td className={bodyCellClass}>{formatNumber(row.shareAmount)}</td>
                      <td className={bodyCellClass}>{formatNumber(row.dividendPerShare)}</td>
                      <td className={twMerge(bodyCellClass, 'font-[550] text-forest-600')}>
                        {currencySymbol} {formatDividendAmount(row.totalPayout)}
                      </td>
                      <td className={bodyCellClass}>
                        {row.files.length > 0 && (
                          <FilesCell
                            files={row.files}
                            onDownload={downloadFile}
                            onOpen={() => setFilesRow(row)}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Fades the row the list is cut off at, so it reads as "there is more below". */}
            {isCutOff && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white via-white/90 to-transparent" />
            )}
          </div>

          <div className="flex h-[62px] shrink-0 items-center justify-between bg-gray-100 px-6">
            <Button
              className="h-9 w-fit bg-transparent px-4 text-sm font-[450] text-gray-700"
              onClick={onClose}
              styleType="NONE"
              type="button"
            >
              <AppFormattedMessage id="CLOSE" />
            </Button>
            {onExport && (
              <Button
                className="flex h-9 w-fit items-center gap-1 border border-gray-300 bg-white px-4 py-2.5"
                disabled={isExporting}
                onClick={onExport}
                styleType="DEFAULT_ROUNDED"
                type="button"
              >
                <ExportIcon className="size-5" />
                <span className="text-sm font-[450] text-gray-700">
                  <AppFormattedMessage id="EXPORT" />
                </span>
              </Button>
            )}
          </div>
        </div>
      </ModalWrapper>
    </>
  );
};
